// ═══════════════════════════════════════════════════════════════════════════════
// QR code for helper registration invite link (/pair-code/:code)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/ToastProvider';
import { buildPairInviteUrl } from '../../lib/pairInviteSession';
import {
  buildPairingInviteShareText,
  canShowPairingInviteShareButton,
  isShareCancelledError,
  sharePairingInviteQr,
} from '../../lib/pairingInviteShare';

type PairingInviteQrCodeProps = {
  code: string;
};

const ShareIcon = ({ className = 'h-6 w-6' }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
    />
  </svg>
);

export const PairingInviteQrCode = ({ code }: PairingInviteQrCodeProps) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { showErrorToast } = useToast();
  const qrWrapRef = useRef<HTMLDivElement>(null);
  const [showShare, setShowShare] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const inviteUrl = buildPairInviteUrl(code);
  const employerNickname = user?.nickname?.trim() || '';

  useEffect(() => {
    const updateVisibility = () => {
      setShowShare(canShowPairingInviteShareButton());
    };
    updateVisibility();

    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    const coarseMq = window.matchMedia('(pointer: coarse)');
    const narrowMq = window.matchMedia('(max-width: 768px)');
    coarseMq.addEventListener('change', updateVisibility);
    narrowMq.addEventListener('change', updateVisibility);
    return () => {
      coarseMq.removeEventListener('change', updateVisibility);
      narrowMq.removeEventListener('change', updateVisibility);
    };
  }, []);

  const handleShare = useCallback(async () => {
    if (isSharing) {
      return;
    }
    const svg = qrWrapRef.current?.querySelector('svg');
    if (!(svg instanceof SVGElement)) {
      showErrorToast(t('pairing.generate.shareError'));
      return;
    }

    setIsSharing(true);
    try {
      await sharePairingInviteQr({
        svgElement: svg,
        title: t('pairing.generate.shareInviteTitle'),
        text: buildPairingInviteShareText({
          language,
          nickname: employerNickname,
          link: inviteUrl,
        }),
      });
    } catch (err) {
      if (isShareCancelledError(err)) {
        return;
      }
      showErrorToast(t('pairing.generate.shareError'));
    } finally {
      setIsSharing(false);
    }
  }, [employerNickname, inviteUrl, isSharing, language, showErrorToast, t]);

  return (
    <div className="mt-4 flex items-center justify-center gap-3">
      <div
        ref={qrWrapRef}
        className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-600"
        role="img"
        aria-label={t('pairing.generate.qrAria')}
      >
        <QRCode
          value={inviteUrl}
          size={168}
          bgColor="#ffffff"
          fgColor="#0f5a42"
          level="M"
        />
      </div>

      {showShare ? (
        <button
          type="button"
          onClick={() => void handleShare()}
          disabled={isSharing}
          className="inline-flex h-14 w-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl border border-primary-300 bg-primary-50 text-primary-800 transition-colors hover:bg-primary-100 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-primary-500 dark:bg-gray-800 dark:text-primary-200 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-900"
          aria-label={t('pairing.generate.shareAria')}
          tabIndex={0}
        >
          {isSharing ? (
            <svg className="h-6 w-6 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <ShareIcon />
          )}
          <span className="text-[10px] font-semibold leading-none">
            {t('pairing.generate.shareLabel')}
          </span>
        </button>
      ) : null}
    </div>
  );
};
